import getDefaultState from './state'

export default {
  // called only during initialization
  setupBatteryState(state, { maxSoc, minSoc, minSocFeedInOpt, minSocPeakShavingOpt, depthOfDischargeCapacity, totalGrossCapacityWh, totalNetCapacityWh, totalAvailableBackupCapacityWh, stateOfCharge, controllableMinSoc }) {
    resetState(state)
    state.maxSoc = parseFloat(maxSoc)
    state.minSoc = parseFloat(minSoc)
    state.depthOfDischargeCapacity = parseFloat(depthOfDischargeCapacity)
    state.totalGrossCapacityWh = parseFloat(totalGrossCapacityWh)
    state.totalNetCapacityWh = parseFloat(totalNetCapacityWh)
    state.totalAvailableBackupCapacityWh = parseFloat(totalAvailableBackupCapacityWh)
    state.stateOfCharge = parseFloat(stateOfCharge) || null
    state.controllableMinSoc = controllableMinSoc
    setSupportedOperatingModes(state, minSocFeedInOpt, minSocPeakShavingOpt, totalAvailableBackupCapacityWh)
  },

  // called when user changes battery capacities, and once during initialization with RTVs
  updateMinSocValues(state, { minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt, minSoc }) {
    const boundedValues = boundedSocValues(state, minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt)
    state.minSocFeedInOpt = boundedValues.minSocFeedInOpt
    state.minSocSelfConsumptionOpt = boundedValues.minSocSelfConsumptionOpt
    state.minSocPeakShavingOpt = boundedValues.minSocPeakShavingOpt
    if (minSoc) state.minSoc = Math.max(Math.min(parseFloat(minSoc), state.maxSoc), state.depthOfDischargeCapacity)
    updateEnabledOperatingModes(state)
  },

  updateMaxGridPowerFeedInOpt(state, maxGridPowerFeedInOpt) {
    state.maxGridPowerFeedInOpt = parseInt(maxGridPowerFeedInOpt)
  },

  updateMaxGridPowerPeakShavingOpt(state, maxGridPowerPeakShavingOpt) {
    state.maxGridPowerPeakShavingOpt = parseInt(maxGridPowerPeakShavingOpt)
  },

  updateCurrentValues(state, data) {
    const KEY_MAP = {
      MIN_SOC: 'currentMinSoc',
      MIN_SOC_FEED_IN_OPT: 'currentMinSocFeedInOpt',
      MIN_SOC_SELF_CONSUMPTION_OPT: 'currentMinSocSelfConsumptionOpt',
      MIN_SOC_PEAK_SHAVING_OPT: 'currentMinSocPeakShavingOpt',
      MAX_GRID_POWER_FEED_IN_OPT: 'currentMaxGridPowerFeedInOpt',
      MAX_GRID_POWER_PEAK_SHAVING: 'currentMaxGridPowerPeakShavingOpt',
    }
    for (const [key, value] of Object.entries(data)) {
      const stateKey = KEY_MAP[key]
      if (stateKey !== undefined) state[stateKey] = value
    }
  },

}

function resetState(state) {
  Object.assign(state, getDefaultState())
}

function setSupportedOperatingModes(state, minSocFeedInOpt, minSocPeakShavingOpt, totalAvailableBackupCapacityWh) {
  state.minSocFeedInOpt = state.maxSoc
  state.minSocSelfConsumptionOpt = state.minSoc

  if (minSocFeedInOpt !== undefined) state.supportedOperatingModes.push('FEED_IN_OPT')

  state.minSocPeakShavingOpt = state.minSocSelfConsumptionOpt
  if (minSocPeakShavingOpt !== undefined) state.supportedOperatingModes.push('PEAK_SHAVING_OPT')

  if (totalAvailableBackupCapacityWh > 0.0) state.supportedOperatingModes.push('BACKUP_ENERGY')
}

function boundedSocValues(state, minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt) {
  let result = {}
  if(!state.supportedOperatingModes.includes('FEED_IN_OPT')) {
    result.minSocFeedInOpt = state.maxSoc
  } else {
    result.minSocFeedInOpt = minSocFeedInOpt != null ? Math.max(Math.min(parseFloat(minSocFeedInOpt), state.maxSoc), state.minSoc) : state.minSocFeedInOpt
  }
  result.minSocSelfConsumptionOpt = minSocSelfConsumptionOpt != null ? Math.max(Math.min(parseFloat(minSocSelfConsumptionOpt), result.minSocFeedInOpt), state.minSoc) : state.minSocSelfConsumptionOpt
  if (!state.supportedOperatingModes.includes('PEAK_SHAVING_OPT')) {
    result.minSocPeakShavingOpt = result.minSocSelfConsumptionOpt
  } else {
    result.minSocPeakShavingOpt = minSocPeakShavingOpt != null ? Math.max(Math.min(parseFloat(minSocPeakShavingOpt), result.minSocSelfConsumptionOpt), state.minSoc) : state.minSocPeakShavingOpt
  }
  return result 
}

function updateEnabledOperatingModes(state) {
  if (state.supportedOperatingModes.includes('FEED_IN_OPT')) { updateEnabledOperatingModeFor(state, 'FEED_IN_OPT', state.minSocFeedInOpt, state.maxSoc) }
  if (state.supportedOperatingModes.includes('SELF_CONSUMPTION_OPT')) { updateEnabledOperatingModeFor(state, 'SELF_CONSUMPTION_OPT', state.minSocSelfConsumptionOpt, state.minSocFeedInOpt) }
  if (state.supportedOperatingModes.includes('PEAK_SHAVING_OPT')) { updateEnabledOperatingModeFor(state, 'PEAK_SHAVING_OPT', state.minSocPeakShavingOpt, state.minSocSelfConsumptionOpt) }
  if (state.supportedOperatingModes.includes('BACKUP_ENERGY')) { updateEnabledOperatingModeFor(state, 'BACKUP_ENERGY', state.minSoc, state.minSocPeakShavingOpt) }
}

function updateEnabledOperatingModeFor(state, mode, lowerBoundary, upperBoundary) {
  if (state.enabledOperatingModes.includes(mode) && lowerBoundary == upperBoundary) {
    state.enabledOperatingModes = state.enabledOperatingModes.filter(enabledMode => enabledMode !== mode)
  } else if (!state.enabledOperatingModes.includes(mode) && lowerBoundary < upperBoundary) {
    state.enabledOperatingModes.push(mode)
  }
}
